import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ShoppingBag,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  Package,
  Store,
  ReceiptText,
  Share2,
  FileDown,
  Film,
  Search,
  Heart,
  ShieldCheck,
  Truck,
  Users,
  Sparkles,
  LayoutGrid,
  Palette,
  BookOpen,
  Sofa,
  Shapes,
  Apple,
} from "lucide-react";
import { toast } from "sonner";
import AdSlot from "../AdSlot";
import LiveNow from "../LiveNow";
import { GoLiveButton } from "../modules/LiveEvents";

const API = process.env.REACT_APP_BACKEND_URL;
const CART_KEY = "nt_store_cart";

export const mediaUrl = (p) => (p && !p.startsWith("http") ? `${API}/api/store/media/${p}` : p);

const focalStyle = (p, src) => {
  const f = (p?.focals || {})[src];
  return f ? { objectPosition: `${f.x}% ${f.y}%` } : undefined;
};

export const loadLocalCart = () => {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || [];
  } catch {
    return [];
  }
};

export const clearStoredCart = () => {
  localStorage.removeItem(CART_KEY);
  fetch(`${API}/api/store/cart`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify([]),
  }).catch((err) => console.debug("cart clear skipped", err));
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const CATEGORY_ICONS = { All: LayoutGrid, "Art & Craft": Palette, Books: BookOpen, "Family Goods": Shapes, Furniture: Sofa, "Meal Time": Apple };
const catIcon = (c) => CATEGORY_ICONS[c] || Package;

const SUBCATEGORIES = [
  { key: "infant", label: "Infant Care", img: "https://images.pexels.com/photos/6692935/pexels-photo-6692935.jpeg?auto=compress&cs=tinysrgb&w=300" },
  { key: "stem", label: "STEM Toys", img: "https://images.pexels.com/photos/6692838/pexels-photo-6692838.jpeg?auto=compress&cs=tinysrgb&w=300" },
  { key: "safety", label: "Safety & Clean", img: "https://images.pexels.com/photos/32720288/pexels-photo-32720288.jpeg?auto=compress&cs=tinysrgb&w=300" },
  { key: "furniture", label: "Classroom Furniture", img: "https://images.pexels.com/photos/8088235/pexels-photo-8088235.jpeg?auto=compress&cs=tinysrgb&w=300" },
];

const TRUST_PILLARS = [
  { icon: ShieldCheck, title: "Verified Center-Grade Safety", text: "Every item is reviewed for classroom durability and child-safety standards." },
  { icon: Users, title: "Shared Family & Bulk Catalog", text: "One catalog for centers and families — bulk tiers for programs, singles for home." },
  { icon: Truck, title: "Instant Digital & Physical Delivery", text: "Digital downloads land instantly; physical goods ship tracked to your door." },
];

export const salePrice = (p) =>
  p.is_free ? 0 : p.discount_pct ? Math.round(p.price * (100 - p.discount_pct)) / 100 : p.price;

const inStock = (p) => p.inventory == null || p.inventory > 0;

const STATUS_STYLES = {
  paid: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
  pending_payment: "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]",
  failed: "bg-[#FCE6E9] border-[#F8C8DC] text-[#B04A6B]",
  expired: "bg-[#FCE6E9] border-[#F8C8DC] text-[#B04A6B]",
  refunded: "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]",
};

const PriceTag = ({ p, large }) => {
  if (p.is_free)
    return (
      <span className={`font-black text-[#2F6031] ${large ? "text-2xl" : ""}`} data-testid={`store-price-${p.id}`}>
        Free
      </span>
    );
  if (p.discount_pct)
    return (
      <span className="flex items-center gap-1.5" data-testid={`store-price-${p.id}`}>
        <span className={`font-black text-[#374151] dark:text-slate-100 ${large ? "text-2xl" : ""}`}>{money(salePrice(p))}</span>
        <span className="text-[11px] font-bold text-[#9CA3AF] line-through">{money(p.price)}</span>
        <span className="px-2 py-0.5 bg-[#FCE6E9] text-[#B04A6B] text-[10px] font-extrabold rounded-full">-{p.discount_pct}%</span>
      </span>
    );
  return (
    <span className={`font-black text-[#374151] dark:text-slate-100 ${large ? "text-2xl" : ""}`} data-testid={`store-price-${p.id}`}>
      {money(p.price)}
    </span>
  );
};

const StockNote = ({ p }) => {
  if (p.inventory == null) return null;
  if (p.inventory <= 0)
    return (
      <span className="inline-block mt-1.5 px-2.5 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold uppercase rounded-full" data-testid={`store-out-of-stock-${p.id}`}>
        Out of stock
      </span>
    );
  if (p.inventory <= 5)
    return (
      <span className="inline-block mt-1.5 px-2.5 py-0.5 bg-[#FFF2D1] border border-[#F5E3B3] text-[#7A5A17] text-[10px] font-extrabold uppercase rounded-full" data-testid={`store-low-stock-${p.id}`}>
        Only {p.inventory} left
      </span>
    );
  return null;
};

// ---------- Product detail modal (gallery, video, sizes, share) ----------
const ProductDetail = ({ p, onClose, onAdd }) => {
  const gallery = [...(p.images || []).map((src) => ({ type: "image", src })), ...(p.video ? [{ type: "video", src: p.video }] : [])];
  const [sel, setSel] = useState(0);
  const [size, setSize] = useState("");
  const needsSize = (p.sizes || []).length > 0;

  const share = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/share/product/${p.id}`);
    toast.success("Share link copied — posts with a full preview card.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#374151]/40 backdrop-blur-sm" onClick={onClose} data-testid="store-detail-modal">
      <div
        className="bg-white rounded-3xl border border-[#FCE6E9] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-[#273244] dark:border-[#374457]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-72 bg-[#FFF9F2] flex items-center justify-center">
          {gallery[sel]?.type === "video" ? (
            <video src={mediaUrl(gallery[sel].src)} controls className="w-full h-full object-contain bg-black" data-testid="store-detail-video" />
          ) : (
            <img src={mediaUrl(gallery[sel]?.src)} alt={p.name} className="w-full h-full object-cover" style={focalStyle(p, gallery[sel]?.src)} data-testid="store-detail-image" />
          )}
          <button onClick={onClose} data-testid="store-detail-close" className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#374151] shadow">
            <X className="w-4 h-4" />
          </button>
          <button onClick={share} data-testid="store-share-btn" title="Copy share link" className="absolute top-3 left-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#A08244] hover:text-[#7A5A17] shadow">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 px-5 pt-4" data-testid="store-detail-gallery">
            {gallery.map((g, i) => (
              <button key={`${g.src}-${i}`} onClick={() => setSel(i)} data-testid={`store-detail-thumb-${i}`} className={`relative w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all ${sel === i ? "border-[#E5A0BC]" : "border-[#FCE6E9] opacity-70 hover:opacity-100"}`}>
                {g.type === "video" ? (
                  <span className="w-full h-full bg-[#374151] flex items-center justify-center">
                    <Film className="w-5 h-5 text-white" />
                  </span>
                ) : (
                  <img src={mediaUrl(g.src)} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block px-2.5 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-extrabold uppercase rounded-full">{p.category}</span>
            {p.is_free && <span className="inline-block px-2.5 py-0.5 bg-[#E2F4E1] text-[#2F6031] text-[10px] font-extrabold uppercase rounded-full">Free</span>}
          </div>
          <h3 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mt-2">{p.name}</h3>
          <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-2">{p.blurb}</p>
          {p.created_by_name && (
            <p className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF] font-bold mt-3" data-testid="store-detail-listed-by">
              {p.created_by_avatar && String(p.created_by_avatar).startsWith("http") ? (
                <img src={p.created_by_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
              ) : (
                <span className="w-5 h-5 rounded-full bg-[#FCE6E9] flex items-center justify-center text-[9px] font-black text-[#E5A0BC]">{p.created_by_name.charAt(0)}</span>
              )}
              Listed by {p.created_by_name}
            </p>
          )}

          {needsSize && (
            <div className="mt-4">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Size</p>
              <div className="flex gap-2 flex-wrap" data-testid="store-detail-sizes">
                {p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`store-size-${s}`} className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${size === s ? "bg-[#B3E0F2] border-[#5CB3DC] text-[#374151]" : "bg-white border-[#FCE6E9] text-[#6B7280] hover:border-[#B3E0F2] dark:bg-[#232D3D] dark:border-[#374457]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <PriceTag p={p} large />
            <StockNote p={p} />
          </div>

          <div className="flex items-center gap-2 mt-5 flex-wrap">
            {p.is_free && p.download_file ? (
              <a
                href={`${API}/api/store/download/${p.id}`}
                data-testid="store-download-btn"
                className="flex items-center gap-1.5 px-5 py-2.5 bg-[#E2F4E1] hover:bg-[#C4E5C2] rounded-full text-xs font-bold text-[#2F6031] transition-all hover:-translate-y-0.5 shadow-sm"
              >
                <FileDown className="w-3.5 h-3.5" /> Download Free
              </a>
            ) : (
              <button
                onClick={() => {
                  if (needsSize && !size) return toast.error("Please pick a size first.");
                  onAdd(p.id, 1, size);
                  onClose();
                }}
                disabled={!inStock(p)}
                data-testid="store-detail-add-btn"
                className="flex items-center gap-1.5 px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#f3b4cd] disabled:opacity-50 rounded-full text-xs font-bold text-[#374151] transition-all hover:-translate-y-0.5 shadow-sm"
              >
                <ShoppingBag className="w-3.5 h-3.5" /> {inStock(p) ? "Add to Cart" : "Out of Stock"}
              </button>
            )}
            <button onClick={share} data-testid="store-detail-share-btn" className="flex items-center gap-1.5 px-5 py-2.5 bg-white hover:bg-[#FFF2D1] border border-[#F5E3B3] rounded-full text-xs font-bold text-[#A08244] transition-colors">
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Storefront({ currentUser, onBack, onRequireSignIn, onHome, onMasterDash }) {
  const [products, setProducts] = useState([]);
  const [catList, setCatList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState(loadLocalCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("shop");
  const [orders, setOrders] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nt_store_saved")) || [];
    } catch {
      return [];
    }
  });
  const [savedOnly, setSavedOnly] = useState(false);

  const toggleSaved = (id) => {
    setSaved((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("nt_store_saved", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    fetch(`${API}/api/store/products`)
      .then((r) => r.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        toast.error("Could not load the store catalog.");
      })
      .finally(() => setLoading(false));
    fetch(`${API}/api/store/categories`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setCatList(data))
      .catch((err) => console.debug("categories fetch skipped", err));
  }, []);

  // Merge server cart on sign-in (server wins when it has items)
  useEffect(() => {
    if (!currentUser?.real) return;
    fetch(`${API}/api/store/cart`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        const serverItems = data.items || [];
        setCart((local) => (serverItems.length ? serverItems : local));
      })
      .catch((err) => console.debug("cart fetch skipped", err));
  }, [currentUser?.real]);

  // Persist cart locally + to the account when signed in
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    if (!currentUser?.real) return;
    fetch(`${API}/api/store/cart`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cart.map(({ product_id, quantity, size }) => ({ product_id, quantity, size: size || "" }))),
    }).catch((err) => console.debug("cart sync skipped", err));
  }, [cart, currentUser?.real]);

  useEffect(() => {
    if (tab !== "orders" || !currentUser?.real) return;
    setOrders(null);
    fetch(`${API}/api/store/orders`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setOrders)
      .catch((err) => {
        console.error(err);
        toast.error("Could not load your orders.");
      });
  }, [tab, currentUser?.real]);

  const categories = useMemo(() => {
    const derived = [...new Set(products.map((p) => p.category))];
    return ["All", ...(catList.length ? catList : derived)];
  }, [products, catList]);
  const visible = useMemo(() => {
    let list = category === "All" ? products : products.filter((p) => p.category === category);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => `${p.name} ${p.blurb || ""} ${p.category}`.toLowerCase().includes(q));
    if (savedOnly) list = list.filter((p) => saved.includes(p.id));
    return list;
  }, [products, category, query, savedOnly, saved]);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const lineKey = (c) => `${c.product_id}::${c.size || ""}`;
  const cartLines = cart.map((c) => ({ ...c, product: byId[c.product_id] })).filter((l) => l.product);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);
  const subtotal = cartLines.reduce((sum, l) => sum + salePrice(l.product) * l.quantity, 0);

  const addToCart = (productId, qty = 1, size = "") => {
    setCart((prev) => {
      const existing = prev.find((c) => c.product_id === productId && (c.size || "") === size);
      if (existing)
        return prev.map((c) => (c.product_id === productId && (c.size || "") === size ? { ...c, quantity: Math.min(50, c.quantity + qty) } : c));
      return [...prev, { product_id: productId, quantity: qty, size }];
    });
    toast.success("Added to cart.");
  };

  const setQty = (key, qty) => {
    if (qty <= 0) return setCart((prev) => prev.filter((c) => lineKey(c) !== key));
    setCart((prev) => prev.map((c) => (lineKey(c) === key ? { ...c, quantity: Math.min(50, qty) } : c)));
  };

  const checkout = async () => {
    if (!cart.length) return;
    if (!currentUser?.real) {
      toast.info("Sign in to check out.");
      onRequireSignIn();
      return;
    }
    setCheckingOut(true);
    try {
      const res = await fetch(`${API}/api/store/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cart.map(({ product_id, quantity, size }) => ({ product_id, quantity, size: size || "" })),
          origin_url: window.location.origin,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Checkout failed");
      if (body.free) {
        setCart([]);
        localStorage.removeItem(CART_KEY);
        setCartOpen(false);
        setTab("orders");
        toast.success("Free items claimed — added to your orders.");
        return;
      }
      window.location.href = body.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="storefront">
      {/* Header */}
      <div className="border-b border-slate-200/60 bg-[#FFF9F2]/90 backdrop-blur-md sticky top-0 z-40 dark:bg-[#232D3D]/85 dark:border-[#374457]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={onHome} data-testid="store-home-logo" title="NestTrack home" className="shrink-0 w-9 h-9 rounded-full border-2 border-[#FCE6E9] overflow-hidden hover:border-[#F8C8DC] transition-colors">
              <img src="/assets/logo.png" alt="NestTrack home" className="w-full h-full object-cover" />
            </button>
            <button
              onClick={onBack}
              data-testid="store-back-btn"
              className="flex items-center gap-1 pl-2 pr-3 py-1.5 text-[11px] font-bold text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors dark:bg-[#273244] dark:border-[#374457]"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                <Store className="w-5 h-5 text-[#5CB3DC]" />
              </span>
              <div>
                <h1 className="font-black text-base font-heading text-[#374151] dark:text-slate-100 leading-tight">NestTrack Store</h1>
                <p className="hidden sm:block text-[10px] text-[#9CA3AF] font-medium">Supplies for centers &amp; families</p>
              </div>
            </div>
          </div>
          {/* Centered catalog search */}
          <div className="hidden md:block flex-1 max-w-md relative">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSavedOnly(false);
                if (tab !== "shop") setTab("shop");
              }}
              placeholder="Search the catalog — toys, furniture, books…"
              className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-xs font-semibold text-[#374151] outline-none focus:border-[#E5A0BC] shadow-[0_2px_10px_rgba(0,0,0,0.04)] placeholder:text-[#B6AEB5] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-100"
              data-testid="store-search"
            />
          </div>
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto [&>button]:shrink-0">
            {(currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master")) && onMasterDash && (
              <button
                onClick={onMasterDash}
                data-testid="store-master-dash-btn"
                title="Back to Master Dashboard"
                className="px-3.5 py-1.5 bg-[#374151] hover:bg-[#1F2937] border border-[#374151] rounded-full text-[11px] font-bold text-white transition-all hover:-translate-y-0.5 shadow-sm"
              >
                <span className="hidden sm:inline">Master Dashboard</span>
                <ShieldCheck className="w-3.5 h-3.5 sm:hidden" />
              </button>
            )}
            <button
              onClick={() => setTab("shop")}
              data-testid="store-tab-shop"
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === "shop" ? "bg-[#B3E0F2] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"}`}
            >
              Shop
            </button>
            <button
              onClick={() => (currentUser?.real ? setTab("orders") : (toast.info("Sign in to view orders."), onRequireSignIn()))}
              data-testid="store-tab-orders"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === "orders" ? "bg-[#B3E0F2] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"}`}
            >
              <ReceiptText className="w-3.5 h-3.5" /> <span className="hidden sm:inline">My Orders</span>
            </button>
            <button
              onClick={() => {
                setSavedOnly((s) => !s);
                if (tab !== "shop") setTab("shop");
              }}
              data-testid="store-saved-btn"
              title="Saved items"
              className={`relative flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all shadow-sm ${savedOnly ? "bg-[#374151] text-white" : "bg-white border border-slate-200 text-[#374151] hover:bg-[#FFF9F2] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"}`}
            >
              <Heart className={`w-3.5 h-3.5 ${savedOnly ? "fill-[#F8C8DC] text-[#F8C8DC]" : ""}`} /> Saved
              {saved.length > 0 && (
                <span data-testid="store-saved-count" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#E5A0BC] text-white text-[10px] font-black flex items-center justify-center">
                  {saved.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setCartOpen(true)}
              data-testid="store-cart-btn"
              className="relative flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#f3b4cd] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm"
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Cart
              {cartCount > 0 && (
                <span data-testid="store-cart-count" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#374151] text-white text-[10px] font-black flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Live Now strip — public live events auto-scroll here too */}
      <LiveNow currentUser={currentUser} page="store" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 flex justify-end" data-testid="store-golive-row">
        <GoLiveButton user={currentUser} location="store" testid="store-go-live" />
      </div>

      {/* Mobile catalog search */}
      <div className="md:hidden max-w-7xl mx-auto px-4 pt-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSavedOnly(false);
              if (tab !== "shop") setTab("shop");
            }}
            placeholder="Search the catalog…"
            className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-xs font-semibold text-[#374151] outline-none focus:border-[#E5A0BC] shadow-[0_2px_10px_rgba(0,0,0,0.04)] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-100"
            data-testid="store-search-mobile"
          />
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "shop" && <AdSlot placement="store" className="mb-6" />}
        {tab === "shop" && (
          <>
            {/* Warm welcome banner + seasonal spotlight */}
            <div className="rounded-[2rem] border border-[#F8C8DC]/40 bg-gradient-to-r from-[#FFF9F2] via-[#F8C8DC]/30 to-[#B3E0F2]/30 p-6 sm:p-10 mb-8 flex flex-col lg:flex-row items-stretch gap-6 dark:border-[#374457]" data-testid="store-hero-banner">
              <div className="flex-1 flex flex-col justify-center">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#B2607F] mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> The NestTrack Marketplace
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1F2937] leading-tight font-heading dark:text-slate-100" data-testid="store-hero-title">
                  Curated supplies for modern childcare centers and growing families.
                </h2>
                <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-3 max-w-md">Center-grade essentials, learning toys, and family goods — one shared catalog, bulk-friendly pricing.</p>
                <div className="mt-5">
                  <button
                    onClick={() => {
                      setCategory("All");
                      setQuery("");
                      setSavedOnly(false);
                      document.querySelector('[data-testid="store-product-grid"]')?.scrollIntoView({ behavior: "smooth" });
                    }}
                    data-testid="store-explore-btn"
                    className="px-6 py-3 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-xs font-black transition-all hover:-translate-y-0.5 shadow-md"
                  >
                    Explore Essentials
                  </button>
                </div>
              </div>
              <div className="lg:w-80 relative overflow-hidden rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.10)] min-h-[180px]">
                <img src="https://images.pexels.com/photos/8924162/pexels-photo-8924162.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Fall classroom setup and sensory kits" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2937]/70 via-transparent" />
                <div className="absolute bottom-0 p-4">
                  <p className="text-white text-sm font-black font-heading">Fall Classroom Setup &amp; Sensory Kits</p>
                  <p className="text-white/80 text-[11px] font-semibold">Seasonal spotlight — refreshed weekly</p>
                </div>
              </div>
            </div>

            {/* Quick sub-category circles */}
            <div className="grid grid-cols-4 gap-3 sm:gap-6 mb-8" data-testid="store-subcat-grid">
              {SUBCATEGORIES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => {
                    setCategory("All");
                    setSavedOnly(false);
                    setQuery(s.key);
                    document.querySelector('[data-testid="store-product-grid"]')?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid={`store-subcat-${s.key}`}
                  className="group flex flex-col items-center gap-2"
                >
                  <span className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 shadow-sm transition-all duration-200 group-hover:scale-105 ${query === s.key ? "border-[#E5A0BC] ring-4 ring-[#F8C8DC]/40" : "border-white dark:border-[#374457]"}`}>
                    <img src={s.img} alt={s.label} className="w-full h-full object-cover" loading="lazy" />
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-extrabold text-[#374151] dark:text-slate-200 text-center leading-tight">{s.label}</span>
                </button>
              ))}
            </div>

            {/* Category pill bar with icons */}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-5" data-testid="store-category-pills">
              {categories.map((c) => {
                const Icon = catIcon(c);
                return (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`store-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all hover:scale-[1.02] active:scale-[0.98] ${category === c ? "bg-[#F8C8DC] text-[#374151] shadow-md" : "bg-white border border-slate-200 text-[#9CA3AF] hover:text-[#374151] dark:bg-[#273244] dark:border-[#374457]"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {c}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1F2937] font-heading dark:text-slate-100" data-testid="store-catalog-title">
                Shop the catalog
              </h2>
              {(query.trim() || savedOnly || category !== "All") && (
                <button
                  onClick={() => {
                    setQuery("");
                    setSavedOnly(false);
                    setCategory("All");
                  }}
                  data-testid="store-clear-filters"
                  className="text-[11px] font-extrabold text-[#B2607F] hover:text-[#374151] transition-colors"
                >
                  Clear filters ×
                </button>
              )}
            </div>

            {loading ? (
              <p className="text-sm text-[#9CA3AF] font-semibold" data-testid="store-loading">Loading catalog…</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="store-product-grid">
                {visible.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`store-product-${p.id}`}
                    className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer dark:bg-[#273244]"
                    onClick={() => setDetail(p)}
                  >
                    <div className="h-44 overflow-hidden bg-[#F9FAFB] relative">
                      <img src={mediaUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" style={focalStyle(p, p.images[0])} loading="lazy" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSaved(p.id);
                        }}
                        data-testid={`store-save-${p.id}`}
                        title={saved.includes(p.id) ? "Remove from saved" : "Save for later"}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur border border-slate-200 flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
                      >
                        <Heart className={`w-4 h-4 ${saved.includes(p.id) ? "fill-[#E5A0BC] text-[#E5A0BC]" : "text-[#9CA3AF]"}`} />
                      </button>
                      {p.video && (
                        <span className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-[#374151]/80 flex items-center justify-center" title="Has video">
                          <Film className="w-3.5 h-3.5 text-white" />
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-block px-2.5 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-extrabold uppercase rounded-full">{p.category}</span>
                        {p.is_free ? (
                          <span className="inline-block px-2.5 py-0.5 bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-extrabold uppercase rounded-full">Free</span>
                        ) : (
                          <span className="inline-block px-2.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] text-[10px] font-extrabold uppercase rounded-full">Center Grade</span>
                        )}
                      </div>
                      <h3 className="font-bold text-sm font-heading text-[#1F2937] dark:text-slate-100 mt-2 leading-snug line-clamp-1">{p.name}</h3>
                      <StockNote p={p} />
                      <div className="flex items-center justify-between mt-3 gap-2">
                        <PriceTag p={p} />
                        {p.is_free && p.download_file ? (
                          <a
                            href={`${API}/api/store/download/${p.id}`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`store-add-${p.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-[#E8F5E9] hover:bg-[#C4E5C2] rounded-full text-[11px] font-black text-[#2E7D32] transition-colors"
                          >
                            <FileDown className="w-3 h-3" /> Get
                          </a>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if ((p.sizes || []).length) return setDetail(p);
                              addToCart(p.id);
                            }}
                            disabled={!inStock(p)}
                            data-testid={`store-add-${p.id}`}
                            className="flex items-center gap-1 px-4 py-2 bg-[#374151] hover:bg-[#1F2937] disabled:opacity-50 rounded-full text-[11px] font-black text-white transition-all hover:-translate-y-0.5 shadow-sm"
                          >
                            <ShoppingBag className="w-3 h-3" /> Add to Nest
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "orders" && (
          <div data-testid="store-orders">
            <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100 mb-6">My Orders</h2>
            {orders === null ? (
              <p className="text-sm text-[#9CA3AF] font-semibold">Loading orders…</p>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-3xl border border-[#FCE6E9] p-10 text-center dark:bg-[#273244] dark:border-[#374457]" data-testid="store-orders-empty">
                <Package className="w-10 h-10 text-[#F8C8DC] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#6B7280] dark:text-slate-300">No orders yet — your purchases will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white rounded-3xl border border-[#FCE6E9] p-5 dark:bg-[#273244] dark:border-[#374457]" data-testid={`store-order-${o.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-black text-sm text-[#374151] dark:text-slate-100">Order {o.id}</p>
                        <p className="text-[11px] text-[#9CA3AF] font-medium">{new Date(o.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 border text-[10px] font-extrabold uppercase rounded-full ${STATUS_STYLES[o.status] || STATUS_STYLES.pending_payment}`} data-testid={`store-order-status-${o.id}`}>
                          {o.status.replace(/_/g, " ")}
                        </span>
                        <span className="font-black text-[#374151] dark:text-slate-100">{o.free ? "Free" : money(o.total)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {o.items.map((i) => (
                        <span key={`${i.productId}-${i.size || ""}`} className="px-2.5 py-1 bg-[#FFF9F2] border border-[#FCE6E9] text-[11px] font-bold text-[#6B7280] rounded-full dark:bg-[#232D3D] dark:border-[#374457] dark:text-slate-300">
                          {i.name}
                          {i.size ? ` (${i.size})` : ""} ×{i.quantity}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {tab === "shop" && (
        <>
          {/* Trust pillars */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14" data-testid="store-trust">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {TRUST_PILLARS.map((t) => (
                <div key={t.title} className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-6 flex items-start gap-4 dark:bg-[#273244]" data-testid={`store-trust-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="w-11 h-11 rounded-full bg-[#E6F2FA] flex items-center justify-center shrink-0">
                    <t.icon className="w-5 h-5 text-[#3D7E9E]" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#1F2937] font-heading dark:text-slate-100">{t.title}</p>
                    <p className="text-xs text-[#6B7280] dark:text-slate-400 mt-1 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Structured dark footer */}
          <footer className="bg-[#1E293B] text-slate-300" data-testid="store-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 sm:grid-cols-4 gap-8">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-3">Center Solutions</p>
                <div className="space-y-2 text-xs font-semibold">
                  {(currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master")) && onMasterDash && (
                    <button onClick={onMasterDash} className="block hover:text-white transition-colors" data-testid="store-footer-master">Master Dashboard</button>
                  )}
                  <button onClick={() => setCategory("Furniture")} className="block hover:text-white transition-colors" data-testid="store-footer-furniture">Classroom Furniture</button>
                  <button onClick={() => setQuery("bulk")} className="block hover:text-white transition-colors" data-testid="store-footer-bulk">Bulk &amp; Program Tiers</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-3">Family Marketplace</p>
                <div className="space-y-2 text-xs font-semibold">
                  <button onClick={() => { setCategory("All"); setSavedOnly(false); setQuery(""); }} className="block hover:text-white transition-colors" data-testid="store-footer-shop">Shop All</button>
                  <button onClick={() => setSavedOnly(true)} className="block hover:text-white transition-colors" data-testid="store-footer-saved">Saved Items</button>
                  <button onClick={() => (currentUser?.real ? setTab("orders") : onRequireSignIn())} className="block hover:text-white transition-colors" data-testid="store-footer-orders">My Orders</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-3">Support</p>
                <div className="space-y-2 text-xs font-semibold">
                  <button onClick={() => window.location.assign("/contact")} className="block hover:text-white transition-colors" data-testid="store-footer-contact">Contact Us</button>
                  <button onClick={() => window.location.assign("/about")} className="block hover:text-white transition-colors" data-testid="store-footer-about">About NestTrack</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-3">Terms</p>
                <div className="space-y-2 text-xs font-semibold">
                  <button onClick={() => window.location.assign("/terms")} className="block hover:text-white transition-colors" data-testid="store-footer-terms">Terms of Service</button>
                  <button onClick={() => window.location.assign("/privacy")} className="block hover:text-white transition-colors" data-testid="store-footer-privacy">Privacy Policy</button>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-700/60 py-4 text-center text-[10px] text-slate-500 font-semibold">NestTrack Store — part of the NestTrack care platform.</div>
          </footer>
        </>
      )}

      {detail && <ProductDetail p={detail} onClose={() => setDetail(null)} onAdd={addToCart} />}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50" data-testid="store-cart-drawer">
          <div className="absolute inset-0 bg-[#374151]/40 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l border-[#FCE6E9] shadow-2xl flex flex-col dark:bg-[#273244] dark:border-[#374457]">
            <div className="flex items-center justify-between p-5 border-b border-[#FCE6E9] dark:border-[#374457]">
              <h3 className="font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#E5A0BC]" /> Your Cart ({cartCount})
              </h3>
              <button onClick={() => setCartOpen(false)} data-testid="store-cart-close" className="w-8 h-8 rounded-full bg-[#FFF9F2] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cartLines.length === 0 && (
                <p className="text-sm font-bold text-[#9CA3AF] text-center py-10" data-testid="store-cart-empty">Your cart is empty.</p>
              )}
              {cartLines.map((l) => (
                <div key={lineKey(l)} className="flex gap-3 bg-[#FFF9F2] rounded-2xl border border-[#FCE6E9] p-3 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`store-cart-line-${l.product_id}`}>
                  <img src={mediaUrl(l.product.images[0])} alt={l.product.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">
                      {l.product.name}
                      {l.size ? <span className="text-[#9CA3AF] font-bold"> · {l.size}</span> : null}
                    </p>
                    <p className="text-[11px] text-[#9CA3AF] font-bold">{l.product.is_free ? "Free" : `${money(salePrice(l.product))} each`}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => setQty(lineKey(l), l.quantity - 1)} data-testid={`store-qty-minus-${l.product_id}`} className="w-6 h-6 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#6B7280]">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-black text-[#374151] dark:text-slate-100 w-5 text-center" data-testid={`store-qty-${l.product_id}`}>{l.quantity}</span>
                      <button onClick={() => setQty(lineKey(l), l.quantity + 1)} data-testid={`store-qty-plus-${l.product_id}`} className="w-6 h-6 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#6B7280]">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => setQty(lineKey(l), 0)} data-testid={`store-remove-${l.product_id}`} className="ml-auto w-6 h-6 rounded-full bg-[#FCE6E9] flex items-center justify-center text-[#B04A6B]">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#FCE6E9] dark:border-[#374457]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#9CA3AF]">Subtotal</span>
                <span className="font-black text-[#374151] dark:text-slate-100" data-testid="store-cart-subtotal">{money(subtotal)}</span>
              </div>
              <p className="text-[10px] text-[#9CA3AF] font-medium mb-4">Taxes calculated securely at checkout. Have a promo code? You can enter it there.</p>
              <button
                onClick={checkout}
                disabled={!cartLines.length || checkingOut}
                data-testid="store-checkout-btn"
                className="w-full py-3 bg-[#F8C8DC] hover:bg-[#f3b4cd] disabled:opacity-50 rounded-full text-sm font-black text-[#374151] transition-all shadow-sm"
              >
                {checkingOut ? "Redirecting to checkout…" : currentUser?.real ? "Secure Checkout" : "Sign In to Check Out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
